import { type Page, success, type UserInfo } from "@iflyrpa/share";
import fetch from "node-fetch";
import type { APIResponse } from "playwright-core";
import type { SteelAction } from "./index";

/**
 * 百家号登录 RPA
 *
 * 流程：
 * 1. 打开百家号登录页
 * 2. 拦截二维码请求，转换为 base64 注入页面（解决跨域问题）
 * 3. 点击"登录/注册百家号"按钮
 * 4. 等待页面跳转到 /builder/rc/home 判断登录成功
 * 5. 通过服务端请求 getPassinfo 接口获取用户信息和 token
 * 6. 收集 cookie 并返回结果
 */
export const rpaAction: SteelAction = async (task, _params) => {
    const reporter = task.reportService?.reportLoginStatus ?? (() => Promise.resolve());

    let page: Page;
    let isSelfClosing = false;
    let handleBrowserDisconnected: (() => void) | undefined;
    const cleanup = async () => {
        if (handleBrowserDisconnected) {
            task._steelBrowser?.off("disconnected", handleBrowserDisconnected);
        }
        if (page && !page.isClosed()) {
            await page.close().catch(() => {});
        }
    };

    // ========== 第一步：启动浏览器并打开登录页 ==========
    try {
        page = await task.createPage({
            // 先进入空白页，确保路由拦截在真正导航前就生效
            url: "about:blank",
        });
        if (!task._session?.id) throw new Error("Session ID 不存在");
        handleBrowserDisconnected = () => {
            if (task.debug && isSelfClosing) {
                task.logger.info("执行结束，关闭浏览器！");
            } else {
                task.logger.info("浏览器被手动释放或意外关闭！");
            }
        };
        task._steelBrowser?.on("disconnected", handleBrowserDisconnected);
    } catch (e) {
        return { code: 414, message: `浏览器启动失败: ${e}`, data: {} };
    }

    // ========== 第二步：生成远程调试地址 ==========
    const pageInfo = await task._client?.sessions.liveDetails(task._session.id);
    const pageId = pageInfo?.pages[0].id ?? "";

    const baseHost = task._session.websocketUrl?.replace(/^wss?:\/\//, "") ?? "";
    const connectAddress = new URLSearchParams({ baseHost, pageId }).toString();

    task.reportService?.settingSessionID?.(task._session?.id || "");

    // 拦截明显无关的重资源，防止未登录阶段内存快速上涨
    await page.route("**/*.webm", (route) => route.abort());
    await page.route("**/*.mp4", (route) => route.abort());

    // 验证码图片跨域处理：转为 data URL 后注入指定元素，确保能渲染
    task.logger.info("正在设置验证码图片拦截和注入...");
    await page.route("**/cgi-bin/genimage**", async (route) => {
        let response: APIResponse | undefined;
        task.logger.info("正在获取验证码图片...");
        for (let i = 0; i < 3; i++) {
            try {
                response = await route.fetch();
                break;
            } catch (error) {
                if (i === 2) {
                    task.logger.error("验证码图片获取失败:", error);
                    await route.abort();
                    return;
                }
                await new Promise((res) => setTimeout(res, 300));
            }
        }

        if (!response) {
            await route.abort();
            return;
        }

        try {
            const buffer = await response.body();
            const mimeType = response.headers()["content-type"] || "image/png";

            await route.fulfill({
                status: response.status(),
                headers: response.headers(),
                body: buffer,
            });

            if (!mimeType.toLowerCase().includes("image")) {
                return;
            }

            const dataUrl = `data:${mimeType};base64,${buffer.toString("base64")}`;
            await page.evaluate((src) => {
                const img = document.querySelector<HTMLImageElement>("#TANGRAM__PSP_4__confirmVerifyCodeImg");
                if (img) img.src = src;
            }, dataUrl);
        } catch (error) {
            task.logger.error("验证码图片渲染失败:", error);
            await route.abort();
        }
    });

    await page.route("**/*", (route) => {
        const request = route.request();
        const url = request.url().toLowerCase();
        const type = request.resourceType();

        if (url.includes("passport.baidu.com/cgi-bin/genimage")) {
            // 交给下一个更具体的 genimage 路由处理（转 data URL 注入）
            return route.fallback();
        }

        if (type === "ping") {
            return route.abort();
        }

        // 活动素材与大图资源对扫码登录无必要，优先拦截
        if (url.includes("pic.rmb.bdstatic.com/activity/")) {
            if (type === "image" || type === "media" || type === "font") {
                return route.abort();
            }
            if ((type === "fetch" || type === "xhr") && url.includes(".json")) {
                return route.abort();
            }
        }

        return route.continue();
    });

    // ========== 第三步：拦截二维码请求并注入 base64 图片 ==========
    // 百家号二维码图片存在跨域限制，需要拦截请求后转为 base64 注入页面
    let hasInjectedQrcode = false;
    await page.route("**/v2/api/qrcode**", async (route, _request) => {
        // 仅首个二维码请求做 base64 注入，避免 data:image 反复注入导致内存堆积
        if (hasInjectedQrcode) {
            await route.continue();
            return;
        }

        let response: APIResponse | undefined;

        // 最多重试 3 次获取二维码
        for (let i = 0; i < 3; i++) {
            try {
                response = await route.fetch();
                break;
            } catch (error) {
                if (i === 2) {
                    task.logger.error("二维码获取失败:", error);
                    await route.abort();
                    return;
                }
                await new Promise((res) => setTimeout(res, 1000));
            }
        }

        if (!response) throw new Error("二维码响应未定义");

        try {
            // 将二维码图片转为 base64 DataURL
            const buffer = await response.body();
            const mimeType = response.headers()["content-type"] || "image/png";

            // 非图片响应不注入，避免错误地生成超大 data URL
            if (!mimeType.toLowerCase().includes("image")) {
                await route.fulfill({
                    status: response.status(),
                    headers: response.headers(),
                    body: buffer,
                });
                return;
            }

            const base64 = buffer.toString("base64");
            const dataUrl = `data:${mimeType};base64,${base64}`;

            // 正常返回响应给页面
            await route.fulfill({
                status: response.status(),
                headers: response.headers(),
                body: buffer,
            });

            // 同时将 base64 图片注入到二维码 img 元素，绕过跨域限制
            await page.evaluate((dataUrl) => {
                const img = document.querySelector<HTMLImageElement>(".tang-pass-qrcode-img");
                if (img) img.src = dataUrl;
            }, dataUrl);
            hasInjectedQrcode = true;
        } catch (error) {
            task.logger.error("二维码处理失败:", error);
            await route.abort();
        }
    });

    // 在所有路由规则挂载后再进入百家号登录页，避免首屏资源在拦截前就加载
    await page.goto("https://baijiahao.baidu.com/builder/theme/bjh/login", {
        timeout: 60 * 1000,
        waitUntil: "commit",
    });

    await reporter(0, "浏览器启动成功，请登录百家号……", { connectAddress });

    // 等待页面 DOM 加载完成
    try {
        await page.waitForLoadState("domcontentloaded", { timeout: 10000 });
        task.logger.info("页面 DOM 加载完成");
    } catch (e) {
        task.logger.warn("页面加载超时，继续执行", { error: String(e) });
    }

    // ========== 第四步：点击登录按钮 ==========
    try {
        await page.getByRole("button", { name: "登录/注册百家号" }).click({ timeout: 15 * 1000 });
        task.logger.info("点击登录按钮成功");
    } catch (e) {
        task.logger.warn("未找到登录按钮或点击失败，可能已经在登录页面", { error: String(e) });
    }

    // ========== 第五步：等待登录成功并获取用户信息 ==========
    let token: string | null = null;
    let userInfo: UserInfo = { uniqueId: "", avatar: "", name: "" };
    try {
        // 等待页面跳转到登录成功页面（不设超时，允许用户慢慢登录）
        await page.waitForURL("**/builder/rc/home**", {
            timeout: 0,
            waitUntil: "domcontentloaded",
        });

        task.logger.info("页面已跳转到登录成功页面");
        await reporter(0, "登录成功，正在获取用户信息...", { stage: "scanQrcode", connectAddress });

        // 通过服务端请求获取用户信息（百家号需要从 Node 端发请求获取 token）
        const cookies = await task._steelBrowserContext?.cookies();
        const cookieHeader = cookies?.map((c) => `${c.name}=${c.value}`).join("; ") || "";

        for (let i = 0; i < 3; i++) {
            try {
                const res = await fetch("https://baijiahao.baidu.com/user-ui/cms/getPassinfo", {
                    method: "GET",
                    headers: { cookie: cookieHeader },
                });

                if (res.ok) {
                    token = res.headers.get("token");
                    const jsonUser = (await res.json()) as {
                        data: { avatar: string; uk: string; displayname: string };
                    };
                    userInfo = {
                        name: jsonUser.data.displayname,
                        avatar: jsonUser.data.avatar,
                        uniqueId: jsonUser.data.uk,
                    };
                    break;
                }

                if (i < 2) await new Promise((res) => setTimeout(res, 500));
            } catch (error) {
                if (i === 2) {
                    task.logger.error("获取用户信息失败", { error: String(error) });
                } else {
                    await new Promise((res) => setTimeout(res, 500));
                }
            }
        }
    } catch (e) {
        task.logger.error("等待登录跳转失败", { error: String(e) });
        isSelfClosing = true;
        await cleanup();
        return { code: 500, message: "登录失败，请重试！", data: { stage: "waitForLogin", connectAddress } };
    }

    // ========== 第六步：收集 cookie 并返回结果 ==========
    const cookie = JSON.stringify(await task._steelBrowserContext?.cookies());

    if (!token) task.logger.warn("未获取到 token，但继续返回结果");
    if (!userInfo.uniqueId) task.logger.warn("未获取到用户信息，但继续返回结果");

    task.logger.info("登录成功", { hasToken: !!token, hasUserInfo: !!userInfo.uniqueId });

    isSelfClosing = true;
    await cleanup();

    return success({ connectAddress, token, cookie, userInfo }, "登录成功！");
};
